/**
 * Clawbert's poke reactions. A small "bark system": lines are drawn from a
 * pool keyed to his real state, never repeat back to back, and hit
 * diminishing returns when you poke him fast (he gets dry, then ignores you).
 *
 * Voice: short, plain, dry, confident. No emoji. Half the lines carry real
 * status or ROI, so a poke is also a status check. Never guilt, ever.
 *
 * Pure module — no imports. The constant/ambient layer (frontend-doctrine
 * character system); substantive moments (postcard, homecoming) generate from
 * the real event log instead.
 */

export type ClawbertState =
  | "idle"
  | "working"
  | "stackReady"
  | "converted"
  | "night"
  | "lowCredits"
  | "returning";

export const POKE_LINES: Record<ClawbertState, readonly string[]> = {
  idle: [
    "Still here. You can go, that's the point.",
    "Nothing needs you right now.",
    "All quiet. I like it that way.",
    "I'm on it. Go do something else.",
  ],
  working: [
    "Mid-thought. Give me a second.",
    "Reading your inbox. It is not thrilling.",
    "Working. This is the boring part.",
    "Busy, in a good way.",
  ],
  stackReady: [
    "Drafts are up top. Your call on the last one.",
    "Three replies waiting for your ok.",
    "Handled the easy ones. The rest is your call.",
    "Done for now. I left the tricky one for you.",
  ],
  converted: [
    "That follow-up you forgot? They booked.",
    "One you'd have lost just came back.",
    "Sold. That's the good kind of ping.",
    "Money that was leaving the table. Not anymore.",
  ],
  night: [
    "Night shift. You should be asleep.",
    "It's late. I've got this.",
    "Go to bed. I don't need one.",
    "Quiet hours. My favorite.",
  ],
  lowCredits: [
    "Running low. I'll pause before I overspend.",
    "Almost out of credits. I won't overreach.",
    "Low on fuel. Nothing runs past the line you set.",
  ],
  returning: [
    "Long time. I kept things running.",
    "Back? Good. Nothing broke.",
    "You were out. The short version's on the stack.",
  ],
} as const;

/** Escalating replies to rapid poking, then he stops answering. */
const ENOUGH_LINES: readonly string[] = ["...", "Okay.", "That's enough poking.", "I have work to do."];

const RAPID_WINDOW_MS = 1200;
const ENOUGH_AFTER = 3; // fast pokes before he gets dry
const IGNORE_AFTER = 6; // fast pokes before he ignores you

export interface PokeController {
  /** Returns Clawbert's line for a poke in the given state, or null to ignore. */
  poke: (state: ClawbertState, now?: number) => string | null;
  reset: () => void;
}

/** One controller per Clawbert instance; holds cooldown + no-repeat memory. */
export function createPokeController(): PokeController {
  let lastPokeAt = -Infinity;
  let rapidCount = 0;
  const lastIndex: Partial<Record<ClawbertState, number>> = {};
  let lastEnoughIndex = -1;

  function pick(pool: readonly string[], prev: number): { line: string; index: number } {
    if (pool.length === 1) return { line: pool[0], index: 0 };
    let index = Math.floor(Math.random() * pool.length);
    if (index === prev) index = (index + 1) % pool.length;
    return { line: pool[index], index };
  }

  return {
    poke(state, now = Date.now()) {
      rapidCount = now - lastPokeAt < RAPID_WINDOW_MS ? rapidCount + 1 : 0;
      lastPokeAt = now;

      if (rapidCount >= IGNORE_AFTER) return null;
      if (rapidCount >= ENOUGH_AFTER) {
        const { line, index } = pick(ENOUGH_LINES, lastEnoughIndex);
        lastEnoughIndex = index;
        return line;
      }
      const pool = POKE_LINES[state];
      const { line, index } = pick(pool, lastIndex[state] ?? -1);
      lastIndex[state] = index;
      return line;
    },
    reset() {
      lastPokeAt = -Infinity;
      rapidCount = 0;
      lastEnoughIndex = -1;
    },
  };
}
